/**
 * ExpenseFormModal
 * 경비 추가/수정 모달
 */

import React, { useState, useEffect } from 'react';
import { TaxExpense, TaxCategory, CreateExpenseRequest } from '../../models/Tax';
import { getTodayKST } from '../../../../utils/dateUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateExpenseRequest) => Promise<boolean>;
  categories: TaxCategory[];
  editItem?: TaxExpense | null;
}

export const ExpenseFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  categories,
  editItem,
}) => {
  const [form, setForm] = useState({
    date: '',
    vendor: '',
    amount: '',
    vat: '',
    total: '',
    category_id: '',
    memo: '',
    card_last4: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editItem) {
      setForm({
        date: editItem.date,
        vendor: editItem.vendor,
        amount: String(editItem.amount),
        vat: String(editItem.vat),
        total: String(editItem.total),
        category_id: editItem.category_id || '',
        memo: editItem.memo || '',
        card_last4: editItem.card_last4 || '',
      });
    } else {
      setForm({
        date: getTodayKST(),
        vendor: '',
        amount: '',
        vat: '',
        total: '',
        category_id: '',
        memo: '',
        card_last4: '',
      });
    }
  }, [editItem, isOpen]);

  const handleAmountChange = (field: 'amount' | 'vat' | 'total', value: string) => {
    const numVal = value.replace(/[^0-9-]/g, '');
    const newForm = { ...form, [field]: numVal };

    // 자동 계산
    if (field === 'total' && numVal) {
      const totalVal = parseInt(numVal);
      const vatVal = Math.round(totalVal / 11);
      newForm.vat = String(vatVal);
      newForm.amount = String(totalVal - vatVal);
    } else if (field === 'amount' && numVal && form.vat) {
      newForm.total = String(parseInt(numVal) + parseInt(form.vat || '0'));
    } else if (field === 'vat' && numVal && form.amount) {
      newForm.total = String(parseInt(form.amount || '0') + parseInt(numVal));
    }

    setForm(newForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date || !form.vendor || !form.total) return;

    setSubmitting(true);
    const success = await onSubmit({
      date: form.date,
      vendor: form.vendor,
      amount: parseInt(form.amount) || 0,
      vat: parseInt(form.vat) || 0,
      total: parseInt(form.total) || 0,
      category_id: form.category_id || undefined,
      memo: form.memo || undefined,
      card_last4: form.card_last4 || undefined,
    });
    setSubmitting(false);

    if (success) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {editItem ? '경비 수정' : '경비 추가'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* 날짜 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">날짜 *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 사용처 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">사용처 *</label>
            <input
              type="text"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              placeholder="예: 스타벅스 강남점"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 금액 */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">합계 *</label>
              <input
                type="text"
                value={form.total}
                onChange={(e) => handleAmountChange('total', e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">공급가액</label>
              <input
                type="text"
                value={form.amount}
                onChange={(e) => handleAmountChange('amount', e.target.value)}
                placeholder="자동계산"
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">부가세</label>
              <input
                type="text"
                value={form.vat}
                onChange={(e) => handleAmountChange('vat', e.target.value)}
                placeholder="자동계산"
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">합계를 입력하면 공급가/부가세가 자동 계산됩니다 (10%)</p>

          {/* 분류 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">분류</label>
            <select
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">미분류</option>
              {categories.filter(c => c.is_active).map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* 카드 끝 4자리 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">카드 끝 4자리</label>
            <input
              type="text"
              value={form.card_last4}
              onChange={(e) => setForm({ ...form, card_last4: e.target.value.slice(0, 4) })}
              placeholder="1234"
              maxLength={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 메모 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
            <textarea
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 버튼 */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? '저장 중...' : editItem ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
